/**
 * GAMIFICATION-CORE (UI) — "Logros": racha de LECTURA (destacada), XP/nivel y
 * badges de constancia con la Palabra. Retrofit OPT-IN y NO punitivo: celebra el
 * hábito de leer y anotar la Escritura como aliento, nunca como presión. Ligas
 * privadas por defecto (no se muestran comparativas entre personas).
 *
 * SEÑAL DE LECTURA: esta app no tiene un feed de `activity` ni un registro de
 * "capítulo leído" sincronizable. La racha se DERIVA como PROXY de las
 * anotaciones locales — la unión de notas, subrayados y marcadores — donde cada
 * día local con alguna anotación cuenta como día de lectura. Proyección PURA y
 * recomputable; no añade tablas ni toca el servidor/sync-rules.
 *
 * Animaciones bajo `motion-safe` (respeta prefers-reduced-motion; guard global).
 */
import { msg } from "@lingui/core/macro";
import { createMemo, For, Show } from "solid-js";
import { Award, BookOpenText, Flame, Trophy } from "lucide-solid";
import {
  bsoGamificationConfig,
  deriveGameState,
  levelProgress,
  queryKeys,
  READING_STREAK_LINE,
  type ReadingRecord,
} from "@bso/shared";
import { createPowerSyncQuery } from "@bso/shared/solid";
import {
  and,
  bible_bookmark,
  bible_highlight,
  bible_note,
  bible_reading_progress,
  bible_verse_highlight,
  eq,
  isNull,
} from "@bso/powersync";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { Card, CardContent } from "@/components/ui/card";
import { PageContainer } from "@/components/ui/page-container";
import { PageHeader } from "@/components/ui/page-header";
import { EmptyState } from "@/components/ui/empty-state";

interface DatedRow {
  createdAt: string | null;
}

/** Etiquetas i18n de los badges (el core solo guarda ids/textos neutros). */
const BADGE_LABELS: Record<
  string,
  { title: ReturnType<typeof msg>; desc: ReturnType<typeof msg> }
> = {
  "first-note": {
    title: msg`First note`,
    desc: msg`Write your first study note.`,
  },
  "devoted-reader": {
    title: msg`Devoted reader`,
    desc: msg`Read or annotate seven days in a row.`,
  },
  "faithful-month": {
    title: msg`Faithful month`,
    desc: msg`Keep a thirty-day reading streak.`,
  },
  scholar: {
    title: msg`Scholar`,
    desc: msg`Write fifty study notes.`,
  },
};

export function AchievementsPage() {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();

  const enabled = () => Boolean(db()) && Boolean(drizzle()) && Boolean(session.userId());

  // ── Anotaciones locales (proxy de la actividad de lectura) ───────────────────
  const notesQuery = createPowerSyncQuery<DatedRow>({
    queryKey: () => queryKeys.gamification.notes(session.userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const u = session.userId();
      if (!dbInstance || !u) {
        return null;
      }
      return dbInstance
        .select({ createdAt: bible_note.created_at })
        .from(bible_note)
        .where(and(eq(bible_note.user_id, u), isNull(bible_note.deleted_at)));
    },
    enabled,
    suspense: false,
  });

  const verseHighlightsQuery = createPowerSyncQuery<DatedRow>({
    queryKey: () => queryKeys.gamification.verseHighlights(session.userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const u = session.userId();
      if (!dbInstance || !u) {
        return null;
      }
      return dbInstance
        .select({ createdAt: bible_verse_highlight.created_at })
        .from(bible_verse_highlight)
        .where(and(eq(bible_verse_highlight.user_id, u), isNull(bible_verse_highlight.deleted_at)));
    },
    enabled,
    suspense: false,
  });

  const groupHighlightsQuery = createPowerSyncQuery<DatedRow>({
    queryKey: () => queryKeys.gamification.groupHighlights(session.userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const u = session.userId();
      if (!dbInstance || !u) {
        return null;
      }
      return dbInstance
        .select({ createdAt: bible_highlight.created_at })
        .from(bible_highlight)
        .where(and(eq(bible_highlight.user_id, u), isNull(bible_highlight.deleted_at)));
    },
    enabled,
    suspense: false,
  });

  const bookmarksQuery = createPowerSyncQuery<DatedRow>({
    queryKey: () => queryKeys.gamification.bookmarks(session.userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const u = session.userId();
      if (!dbInstance || !u) {
        return null;
      }
      return dbInstance
        .select({ createdAt: bible_bookmark.created_at })
        .from(bible_bookmark)
        .where(and(eq(bible_bookmark.user_id, u), isNull(bible_bookmark.deleted_at)));
    },
    enabled,
    suspense: false,
  });

  // Días de plan de lectura completados: señal de lectura EXPLÍCITA (no proxy).
  const planDaysQuery = createPowerSyncQuery<DatedRow>({
    queryKey: () => queryKeys.gamification.planDays(session.userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const u = session.userId();
      if (!dbInstance || !u) {
        return null;
      }
      return dbInstance
        .select({ createdAt: bible_reading_progress.created_at })
        .from(bible_reading_progress)
        .where(
          and(eq(bible_reading_progress.user_id, u), isNull(bible_reading_progress.deleted_at)),
        );
    },
    enabled,
    suspense: false,
  });

  const records = createMemo<ReadingRecord[]>(() => {
    const out: ReadingRecord[] = [];
    for (const r of notesQuery.data ?? []) {
      out.push({ kind: "note", createdAt: r.createdAt });
    }
    for (const r of verseHighlightsQuery.data ?? []) {
      out.push({ kind: "highlight", createdAt: r.createdAt });
    }
    for (const r of groupHighlightsQuery.data ?? []) {
      out.push({ kind: "highlight", createdAt: r.createdAt });
    }
    for (const r of bookmarksQuery.data ?? []) {
      out.push({ kind: "bookmark", createdAt: r.createdAt });
    }
    for (const r of planDaysQuery.data ?? []) {
      out.push({ kind: "planDay", createdAt: r.createdAt });
    }
    return out;
  });

  const state = createMemo(() => deriveGameState(records()));
  const progress = createMemo(() => levelProgress(state().xp));
  const streak = createMemo(() => state().streaks[READING_STREAK_LINE]?.current ?? 0);
  const bestStreak = createMemo(() => state().streaks[READING_STREAK_LINE]?.best ?? 0);
  const earned = createMemo(() => new Set(state().badges));

  return (
    <PageContainer maxWidth="3xl">
      <div class="space-y-6" data-testid="achievements-page">
        <PageHeader
          icon={Trophy}
          title={t(msg`Achievements`)}
          description={t(msg`Your walk in the Word — private by default.`)}
        />

        {/* Racha de LECTURA — destacada (fit alto: la constancia es el corazón). */}
        <Card class="border-primary/40 overflow-hidden">
          <CardContent class="pt-6 flex items-center gap-5">
            <Flame class="h-20 w-20 shrink-0 text-primary motion-safe:animate-pulse" />
            <div class="min-w-0">
              <div class="text-sm font-medium uppercase tracking-wide text-muted-foreground">
                {t(msg`Reading streak`)}
              </div>
              <div class="flex items-baseline gap-2">
                <span class="text-5xl font-black text-primary" data-testid="streak-current">
                  {streak()}
                </span>
                <span class="text-lg font-semibold text-muted-foreground">
                  {t(msg`days in a row`)}
                </span>
              </div>
              <div class="mt-1 text-xs text-muted-foreground" data-testid="streak-best">
                {t(msg`Best`)}: {bestStreak()} {t(msg`days`)}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Nivel + barra de XP */}
        <Card>
          <CardContent class="pt-6">
            <div class="flex items-center justify-between mb-2">
              <span class="text-sm font-medium text-muted-foreground">{t(msg`Level`)}</span>
              <span class="text-2xl font-extrabold text-primary" data-testid="player-level">
                {state().level}
              </span>
            </div>
            <div class="h-3 w-full rounded-full bg-secondary/25 overflow-hidden">
              <div
                class="h-full rounded-full bg-primary motion-safe:transition-[width] motion-safe:duration-500"
                style={{ width: `${Math.round(progress().ratio * 100)}%` }}
              />
            </div>
            <div class="mt-2 flex items-center justify-between text-xs text-muted-foreground">
              <span data-testid="xp-total">
                {state().xp} {t(msg`XP`)}
              </span>
              <span>
                {progress().xpToNext} {t(msg`XP to next level`)}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Badges */}
        <div>
          <h2 class="text-lg font-bold mb-3 flex items-center gap-2">
            <Award class="h-5 w-5 text-primary" />
            {t(msg`Badges`)}
          </h2>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <For each={bsoGamificationConfig.badges ?? []}>
              {(badge) => {
                const isEarned = () => earned().has(badge.id);
                const label = BADGE_LABELS[badge.id];
                return (
                  <Card
                    class={
                      isEarned()
                        ? "border-primary/40"
                        : "opacity-55 grayscale motion-safe:transition-opacity"
                    }
                    data-testid={`badge-${badge.id}`}
                    data-earned={isEarned() ? "true" : "false"}
                  >
                    <CardContent class="pt-6 flex items-start gap-3">
                      <Award
                        class={isEarned() ? "h-6 w-6 text-primary shrink-0" : "h-6 w-6 shrink-0"}
                      />
                      <div>
                        <div class="font-semibold">{label ? t(label.title) : badge.id}</div>
                        <Show when={label}>
                          {(l) => <p class="text-xs text-muted-foreground mt-0.5">{t(l().desc)}</p>}
                        </Show>
                      </div>
                    </CardContent>
                  </Card>
                );
              }}
            </For>
          </div>
        </div>

        <Show when={state().xp === 0}>
          <EmptyState
            icon={BookOpenText}
            title={t(msg`No progress yet`)}
            description={t(msg`Read and annotate Scripture to start a streak and earn badges.`)}
          />
        </Show>
      </div>
    </PageContainer>
  );
}
